import { useEffect, useRef, type ReactNode } from 'react'
import './Prose.css'

export type ProseTableLayout = 'cards' | 'scroll'

export interface ProseHeading {
  /** L'`id` posé sur le titre — c'est l'ancre vers laquelle un sommaire renvoie. */
  id: string
  /** Le texte du titre, tel qu'il est écrit dans le contenu. */
  text: string
}

export interface ProseProps {
  /** Le HTML à mettre en forme. Typiquement le rendu d'un Markdown rédigé ailleurs. */
  children: ReactNode
  /**
   * Le sort du tableau en écran étroit. `cards` (défaut) transforme chaque
   * ligne en fiche et répète le libellé de colonne devant chaque valeur ;
   * `scroll` conserve le tableau et le fait défiler horizontalement.
   */
  tableLayout?: ProseTableLayout
  /**
   * Rappelé quand la liste des `h2` change, pour construire un sommaire.
   * Les `id` sont déjà posés quand il est appelé.
   */
  onHeadings?: (headings: ProseHeading[]) => void
}

function slugify(texte: string): string {
  return texte
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Met en forme du texte long — le seul composant de cette bibliothèque qui
 * stylise du HTML qu'il ne produit pas. Les pages légales reçoivent du Markdown
 * écrit depuis le back-office : ni la longueur, ni l'ordre, ni les éléments
 * employés ne sont connus à l'avance.
 *
 * Le traitement est donc **complet et non différentiel** : il ne tient rien pour
 * acquis du navigateur, parce que la feuille globale du site remet à zéro marges,
 * retraits et puces de tous les éléments. Une liste écrite en back-office perdrait
 * sinon ses puces hors de la zone visible.
 *
 * Deux choses sont faites après le rendu, que le contenu ne porte pas lui-même :
 * un `id` est posé sur chaque `h2` (d'où le sommaire), et le libellé de colonne
 * est recopié sur chaque cellule depuis le `thead` — c'est ce qui permet la mise
 * en fiches en écran étroit, quel que soit le nombre de lignes ou de colonnes.
 */
export function Prose({ children, tableLayout = 'cards', onHeadings }: ProseProps) {
  const racineRef = useRef<HTMLDivElement>(null)
  // Le rappel n'entre pas dans les dépendances de l'effet : un parent qui le
  // recrée à chaque rendu relancerait l'effet en boucle. La ref garde le
  // dernier reçu sans provoquer de nouveau passage.
  const onHeadingsRef = useRef(onHeadings)
  onHeadingsRef.current = onHeadings
  const signatureRef = useRef<string>('')

  useEffect(() => {
    const racine = racineRef.current
    if (!racine) return

    racine.querySelectorAll('table').forEach((tableau) => {
      const libelles = Array.from(tableau.querySelectorAll('thead th')).map((th) =>
        (th.textContent ?? '').trim(),
      )
      tableau.querySelectorAll('tbody tr').forEach((ligne) => {
        Array.from(ligne.children).forEach((cellule, index) => {
          if (libelles[index]) cellule.setAttribute('data-label', libelles[index])
        })
      })
    })

    const titres = Array.from(racine.querySelectorAll('h2'))
    const vus = new Map<string, number>()
    titres.forEach((titre) => {
      if (titre.id) return
      const base = slugify(titre.textContent ?? '') || 'section'
      // Deux titres identiques produiraient deux ancres identiques, et le
      // sommaire renverrait deux fois au même endroit.
      const rang = vus.get(base) ?? 0
      vus.set(base, rang + 1)
      titre.id = rang === 0 ? base : `${base}-${rang + 1}`
    })

    const entetes: ProseHeading[] = titres.map((titre) => ({
      id: titre.id,
      text: (titre.textContent ?? '').trim(),
    }))
    const signature = entetes.map((e) => `${e.id}#${e.text}`).join('|')
    if (signature !== signatureRef.current) {
      signatureRef.current = signature
      onHeadingsRef.current?.(entetes)
    }
  })

  return (
    <div className="azoya-prose" data-table-layout={tableLayout} ref={racineRef}>
      {children}
    </div>
  )
}
